import SearchIcon from "@mui/icons-material/SearchOutlined";
import { Tooltip } from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { ReactComponent as DownloadIcon } from "../../assets/images/download-icon.svg";
import { ReactComponent as ToggleBarIcon } from "../../assets/images/toggle-arrow.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import { ReactComponent as DashboardIcon } from "../../assets/images/dashboard-icon.svg";
import { ReactComponent as ViewIcon } from "../../assets/images/view-icon.svg";
import { ReactComponent as CheckIcon } from "../../assets/images/check-icon.svg";
import CButton from "../../components/CButton";
import CDataGrid from "../../components/CDataGrid";
import CTextField from "../../components/CTextField";
import CommentDialog from "../../components/CommentDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { supabase } from "../../config/supabaseClient";
import { messages } from "../../constant/alertMessages";
import { ROWS_PER_PAGE_OPTIONS, formatZAR, getLocalData } from "../../constant/utils/commonUtils";
import { fetchConfiguration } from "../../slices/configSlice";
import {
  approvePlanOptimizer,
  deletePlanOptimizer,
  enablePlanOptimizerDashboardPreview,
  fetchPlanOptimizerHistory,
  fetchPlanStaleStatus,
} from "../../slices/planOptimizerSlice";
import {
  PLAN_OPTIMIZER_STALE_CATEGORY_LABELS,
  statuses,
  roleType,
} from "../../constant/constants";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import { fetchYearId } from "../../slices/yearSlice";
import AllocationSummaryModal from "./AllocationSummaryModal";
import PlanOptimizerModal from "./PlanOptimizerModal";
import {
  isPlanTransformationComplete,
} from "../../utils/planOptimizerV2Service";

const formatPlanOptimizerStaleReasons = (reasons) => {
  if (!Array.isArray(reasons) || reasons.length === 0) {
    return "";
  }
  return reasons
    .map((key) => PLAN_OPTIMIZER_STALE_CATEGORY_LABELS[key] || key)
    .join("; ");
};

const isPlanOptimizerCellBlank = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (typeof value === "string" && value.trim() === "");

const getPlanOptimizerCellValue = (row, field) => {
  if (!row || !field?.config) return undefined;
  let value = field.config.isAdditionalField
    ? (row.allocated_additional_field?.[field.fieldName] ?? row[field.fieldName])
    : row[field.fieldName];
  if (
    field.config.isTrainingCostField &&
    (value === undefined || value === null) &&
    row.cost != null
  ) {
    value = row.cost;
  }
  return value;
};

const getPlanOptimizerSortValue = (row, field) => {
  if (!row || !field?.config) return "";
  const value = getPlanOptimizerCellValue(row, field);

  if (field.config.isTrainingCostField || field.type === "number") {
    if (isPlanOptimizerCellBlank(value)) return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  const fieldTypeLc = String(field.type || "").toLowerCase();
  const isDateTypeField =
    fieldTypeLc === "date" ||
    fieldTypeLc === "datetime" ||
    fieldTypeLc === "datepicker";
  if (isDateTypeField) {
    if (isPlanOptimizerCellBlank(value)) return null;
    const m = moment(value);
    return m.isValid() ? m.valueOf() : null;
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "";
  }
  if (isPlanOptimizerCellBlank(value)) return "";
  return String(value);
};

const comparePlanOptimizerValues = (a, b, direction = "asc") => {
  const multiplier = direction === "desc" ? -1 : 1;
  if (a == null && b == null) return 0;
  if (a == null) return multiplier;
  if (b == null) return -multiplier;
  if (typeof a === "number" && typeof b === "number") {
    return (a - b) * multiplier;
  }
  return (
    String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    }) * multiplier
  );
};

const PlanOptimizer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [isOptimizerModalOpen, setIsOptimizerModalOpen] = useState(false);
  const [regenerateValues, setRegenerateValues] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [selectedSummaryData, setSelectedSummaryData] = useState(null);
  const [deleteDialogState, setDeleteDialogState] = useState({
    id: null,
    show: false,
    submitting: false,
  });
  const [approveDialogState, setApproveDialogState] = useState({
    plan: null,
    show: false,
    comment: "",
    submitting: false,
  });
  const [latestPlanUpdate, setLatestPlanUpdate] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const companyId = getLocalData("sdfUser")?.company_id;
  const { history, loading: historyLoading, staleByPlanId } = useSelector(
    (state) => state.planOptimizer,
  );
  const { setaTemplate, fileStructure } = useSelector((state) => state.setaTemplate);
  const { configData } = useSelector((state) => state.config);

  const refreshStaleForCompletedPlans = useCallback(
    (plans) => {
      if (!plans?.length) return;
      plans.forEach((plan) => {
        if (plan.approved_at) return;
        if (isPlanTransformationComplete(plan)) {
          dispatch(fetchPlanStaleStatus(plan.id));
        }
      });
    },
    [dispatch],
  );

  // Re-check stale when history loads
  useEffect(() => {
    refreshStaleForCompletedPlans(history);
  }, [history, refreshStaleForCompletedPlans]);

  // Re-check stale when user returns to this tab after editing forms elsewhere
  useEffect(() => {
    const onFocus = () => refreshStaleForCompletedPlans(history);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [history, refreshStaleForCompletedPlans]);

  // Fetch history and config on mount
  useEffect(() => {
    if (companyId) {
      dispatch(fetchPlanOptimizerHistory(companyId));
      
      // Fetch year and config
      dispatch(fetchYearId()).then((res) => {
        if (!res.error) {
          const yearId = res.payload;
          if (!configData) {
            dispatch(fetchConfiguration({ yearId }));
          }
        }
      });
    }
  }, [companyId, dispatch, configData]);

  // Auto-load first history item when history loads (similar to Skills Planner)
  const [lastAutoLoadedPlanId, setLastAutoLoadedPlanId] = useState(null);
  
  useEffect(() => {
    if (!history || history.length === 0) return;
    
    const latestHistory = history[0];
    if (latestHistory && latestHistory.id !== lastAutoLoadedPlanId && rows.length === 0) {
      // Auto-load the latest plan if it's different from what we last loaded
      setLastAutoLoadedPlanId(latestHistory.id);
      setSelectedPlan(latestHistory); // Set selected plan for budget display
      // Use setTimeout to avoid calling handleViewPlan during render
      setTimeout(() => {
        handleViewPlan(latestHistory);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, lastAutoLoadedPlanId, rows.length]);

  // Track latest plan for budget footer
  useEffect(() => {
    if (!history || history.length === 0) {
      setLatestPlanUpdate(null);
      return;
    }

    setLatestPlanUpdate({ plan: history[0] });
    setIsLoading(false);
  }, [history]);

  // Fetch seta template for columns
  useEffect(() => {
    if (companyId && !setaTemplate.length) {
      dispatch(fetchSetaTemplateByCompanyId(companyId));
    }
  }, [companyId, dispatch, setaTemplate.length]);

  //==================
  // Generate columns from setaTemplate (generatePlan + additional field columns)
  //==================
  useEffect(() => {
    if (!setaTemplate.length) return;

    let newColumns = [];
    setaTemplate.forEach((field, index) => {
      if (field.config.generatePlan) {
        const colId = `plan_${index}`;
        newColumns.push({
          headerName: field.fieldName,
          field: colId,
          minWidth: field.fieldName.length > 10 ? 200 : 100,
          id: colId,
          valueGetter: (_value, row) =>
            getPlanOptimizerSortValue(row, field),
          renderCell: ({ row }) => {
            const value = getPlanOptimizerCellValue(row, field);
            const fieldTypeLc = String(field.type || "").toLowerCase();
            const isDateTypeField =
              fieldTypeLc === "date" ||
              fieldTypeLc === "datetime" ||
              fieldTypeLc === "datepicker";
            // Coerce string numbers so "0", "5" etc. display correctly
            if (field.config.isTrainingCostField) {
              const num = Number(value);
              return (
                <p>
                  {!isPlanOptimizerCellBlank(value) && !Number.isNaN(num)
                    ? formatZAR(num)
                    : "-"}
                </p>
              );
            }
            if (isDateTypeField) {
              if (isPlanOptimizerCellBlank(value)) return <p>-</p>;
              const m = moment(value);
              return (
                <p>{m.isValid() ? m.format("DD/MM/YYYY") : String(value)}</p>
              );
            }
            if (field.type === "number") {
              if (!isPlanOptimizerCellBlank(value)) {
                const num = Number(value);
                if (!Number.isNaN(num)) return <p>{num}</p>;
              }
            }
            if (Array.isArray(value)) {
              return value.length ? value.join(", ") : "-";
            }
            if (isPlanOptimizerCellBlank(value)) return <p>-</p>;
            return <p>{String(value)}</p>;
          },
        });
      }
    });
    // Additional field columns (only for fields that are not already in generatePlan, to avoid duplicate headers)
    const additionalFieldColumns = (setaTemplate || [])
      .filter((f) => f?.config?.isAdditionalField && !f?.config?.generatePlan)
      .map((field, idx) => {
        const colId = `additional_${idx}`;
        const isNum = field.type === "number" || field?.config?.isTrainingCostField;
        return {
          headerName: field.fieldName,
          field: colId,
          minWidth: field.fieldName.length > 10 ? 200 : 100,
          id: colId,
          valueGetter: (_value, row) =>
            getPlanOptimizerSortValue(row, field),
          renderCell: ({ row }) => {
            const raw = getPlanOptimizerCellValue(row, field);
            const fieldTypeLc = String(field.type || "").toLowerCase();
            const isDateTypeField =
              fieldTypeLc === "date" ||
              fieldTypeLc === "datetime" ||
              fieldTypeLc === "datepicker";
            if (isNum && (raw === 0 || raw === "0")) return <p>{Number(raw)}</p>;
            if (isNum && !isPlanOptimizerCellBlank(raw)) {
              const num = Number(raw);
              if (!Number.isNaN(num))
                return (
                  <p>{field?.config?.isTrainingCostField ? formatZAR(num) : num}</p>
                );
            }
            if (isPlanOptimizerCellBlank(raw)) return <p>-</p>;
            if (isDateTypeField) {
              const m = moment(raw);
              return (
                <p>{m.isValid() ? m.format("DD/MM/YYYY") : String(raw)}</p>
              );
            }
            if (Array.isArray(raw)) {
              return raw.length ? raw.join(", ") : "-";
            }
            return <p>{String(raw)}</p>;
          },
        };
      });
    setColumns([...newColumns, ...additionalFieldColumns]);
  }, [setaTemplate]);

  const displayRows = useMemo(() => {
    if (!sortModel.length || !rows.length || !columns.length) {
      return rows;
    }

    const { field, sort } = sortModel[0];
    const column = columns.find((col) => col.field === field);
    if (!column?.valueGetter) {
      return rows;
    }

    const getValue = (row) => column.valueGetter(undefined, row);
    return [...rows].sort((rowA, rowB) =>
      comparePlanOptimizerValues(getValue(rowA), getValue(rowB), sort),
    );
  }, [rows, sortModel, columns]);

  // Handle view plan - load plan data into grid
  // Load plan data from plan_optimizer_rows table
  const handleViewPlan = async (plan) => {
    try {
      setIsLoading(true);
      setSelectedPlan(plan); // Track the currently selected plan
      setSortModel([]);

      // Load rows from plan_optimizer_rows table
      const { data: allRows, error: rowsError, count } = await supabase
        .from("plan_optimizer_rows")
        .select("data, id", { count: "exact" })
        .eq("plan_optimizer_id", plan.id);

      if (rowsError) {
        console.error("Error loading rows:", rowsError);
        toast.error("Error loading plan data: " + (rowsError.message || "Unknown error"));
        setIsLoading(false);
        return;
      }

      const planRows = (allRows || []).map((item) => ({
        ...item.data,
        id: item.id,
      }));

      setRows(planRows);

      const rowCount = count ?? planRows.length;
      if (rowCount === 0) {
        toast.info("No training data is available for this plan.");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading plan:", error);
      toast.error("Error loading plan: " + (error.message || "Unknown error"));
      setIsLoading(false);
    }
  };

  const handleViewDashboard = async (plan) => {
    if (!isPlanTransformationComplete(plan)) {
      toast.error("Plan rows must be saved before opening dashboard preview.");
      return;
    }
    try {
      const result = await dispatch(
        enablePlanOptimizerDashboardPreview(plan.id),
      );
      if (result.error) {
        toast.error(
          result.payload || "Could not add plan to dashboard preview.",
        );
        return;
      }
      navigate(
        `/dashboard?source=optimized&sourceReferenceId=${plan.id}`,
      );
    } catch (error) {
      toast.error(error?.message || "Could not open dashboard preview.");
    }
  };

  const resetDeleteDialogState = () =>
    setDeleteDialogState({
      id: null,
      show: false,
      submitting: false,
    });

  // Handle delete plan - show confirmation dialog
  const handleDeletePlan = (plan) => {
    setDeleteDialogState({
      id: plan.id,
      show: true,
      submitting: false,
    });
  };

  const handleApprovePlan = async (plan) => {
    const staleResult = await dispatch(fetchPlanStaleStatus(plan.id));
    const staleInfo = staleResult.payload;
    if (staleInfo?.stale) {
      const reasons =
        formatPlanOptimizerStaleReasons(staleInfo.reasons) ||
        "Live data changed since plan generation.";
      toast.error(`Cannot approve stale plan: ${reasons}`);
      return;
    }
    setApproveDialogState({ plan, show: true, comment: "", submitting: false });
  };

  const handleConfirmApprovePlan = async () => {
    if (!approveDialogState.comment?.trim()) {
      toast.error("Please provide a comment for approval.");
      return;
    }
    setApproveDialogState((s) => ({ ...s, submitting: true }));
    try {
      const result = await dispatch(
        approvePlanOptimizer({
          planId: approveDialogState.plan.id,
          comment: approveDialogState.comment.trim(),
        }),
      ).unwrap();
      const updated = result?.forms_updated ?? 0;
      const demoted = result?.forms_demoted ?? 0;
      const skipped = result?.forms_skipped ?? 0;
      const removed = result?.trainings_removed ?? 0;
      const trainingsUpdated = result?.trainings_updated ?? 0;
      if (updated === 0 && demoted === 0) {
        toast.error(
          skipped > 0
            ? `No training forms were updated. ${skipped} form(s) were skipped because they are not in Recommended or Approved status on Manage Training Needs.`
            : "No training forms were updated.",
       );
      } else {
        const parts = [
          updated > 0 ? `${updated} form(s) updated in plan` : null,
          demoted > 0 ? `${demoted} form(s) returned to Recommended (not in plan)` : null,
          skipped > 0 ? `${skipped} skipped` : null,
          removed > 0 ? `${removed} training(s) removed (not in plan)` : null,
          trainingsUpdated > 0 ? `${trainingsUpdated} allocation(s) updated` : null,
        ].filter(Boolean);
        toast.success(`Plan approved. ${parts.join("; ")}.`);
      }
      setApproveDialogState({
        plan: null,
        show: false,
        comment: "",
        submitting: false,
      });
      if (companyId) {
        dispatch(fetchPlanOptimizerHistory(companyId));
      }
      if (selectedPlan?.id === approveDialogState.plan.id) {
        handleViewPlan({ ...approveDialogState.plan, approved_at: new Date().toISOString() });
      }
    } catch (err) {
      toast.error(err || "Failed to approve plan");
      setApproveDialogState((s) => ({ ...s, submitting: false }));
    }
  };

  // Confirm delete plan
  const handleConfirmDeletePlan = async () => {
    const { id } = deleteDialogState;
    setDeleteDialogState((s) => ({ ...s, submitting: true }));
    try {
      await dispatch(deletePlanOptimizer(id)).unwrap();
      toast.success(messages.DELETED_SUCCESSFULLY);

      // Clear rows if deleted plan was currently viewed
      if (rows.length > 0) {
        setRows([]);
      }

      // Clear selected plan if it was deleted
      if (selectedPlan && selectedPlan.id === id) {
        setSelectedPlan(null);
      }

      resetDeleteDialogState();
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("Error deleting plan: " + (error.message || "Unknown error"));
      setDeleteDialogState((s) => ({ ...s, submitting: false }));
    }
  };

  //==================
  // Handle export to Excel (exportSheet fields + additional fields)
  //==================
  const handleExport = async () => {
    if (!rows.length) {
      toast.error("No data to export");
      return;
    }

    if (!setaTemplate.length) {
      toast.error("SETA template not loaded");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const sheetsList = fileStructure?.sheets || [];
      const empSheetConfig =
        sheetsList.find((s) => s.sheetRole === "employeeImport") ||
        sheetsList.find((s) => s.sheetName === "Employee") ||
        sheetsList.find(
          (s) =>
            /^employee/i.test(s.sheetName?.trim() || "") ||
            (!s.fields?.some((f) => f?.config?.generatePlan) &&
              s.fields?.some((f) => f?.config?.employeeImport))
        );
      const wspSheetConfig =
        sheetsList.find((s) => s.sheetRole === "planExport") ||
        sheetsList.find((s) => s.sheetName === "WSP") ||
        sheetsList.find((s) => s.fields?.some((f) => f?.config?.generatePlan)) ||
        sheetsList[0];

      if (sheetsList.length > 1 && empSheetConfig && wspSheetConfig && empSheetConfig !== wspSheetConfig) {
        // Tab 1: Employee
        const empSheetTabName = empSheetConfig.sheetName || "Employee";
        const empOutputFields = (empSheetConfig?.fields || []).filter(
          (f) => f.config?.exportSheet && !f.config?.isDepartmentNameField && !f.config?.isEmailField
        );

        const { data: empUsers, error: empErr } = await supabase
          .from("company_user")
          .select("data, emp_id, first_name, last_name, gender, race, disability_status, employment_type")
          .eq("status", statuses.ACTIVE)
          .eq("company_id", companyId)
          .eq("role", roleType.EMPLOYEE)
          .not("data", "is", null);

        if (!empErr && empUsers?.length) {
          const empExportData = empUsers.map((user) => {
            const d = user.data || {};
            const row = {};
            empOutputFields.forEach((field) => {
              const fn = field.fieldName;
              row[fn] = d[fn] !== undefined && d[fn] !== null && d[fn] !== "" ? d[fn] : "-";
            });
            return row;
          });
          const wsEmployee = XLSX.utils.json_to_sheet(empExportData);
          XLSX.utils.book_append_sheet(wb, wsEmployee, empSheetTabName.trim() || "Employee");
        }

        // Tab 2: WSP
        const wspSheetTabName = wspSheetConfig.sheetName || "WSP";
        const wspOutputFields = (wspSheetConfig?.fields || []).filter(
          (f) => f.config?.exportSheet && !f.config?.isDepartmentNameField && !f.config?.isEmailField
        );

        let skilledInfo;
        wspOutputFields.forEach((fieldConfig) => {
          if (fieldConfig?.config?.isSkillInfoField) {
            skilledInfo = fieldConfig.fieldName;
          }
        });

        const isCellBlank = (v) =>
          v === undefined ||
          v === null ||
          v === "" ||
          (typeof v === "string" && v.trim() === "");

        const wspExportData = rows.map((record) => {
          const filteredEntry = {};
          wspOutputFields.forEach((fieldConfig) => {
            const field = fieldConfig?.fieldName;
            if (!field) return;
            const isAdditionalField = fieldConfig?.config?.isAdditionalField;
            const value = isAdditionalField
              ? (record.allocated_additional_field?.[field] ?? record[field])
              : record[field];
            const isTrainingCostField = fieldConfig?.config?.isTrainingCostField;
            const isNumberField = fieldConfig?.type === "number";

            if (isTrainingCostField || isNumberField) {
              const num = Number(value);
              filteredEntry[field] = !isCellBlank(value) && !Number.isNaN(num) ? num : "-";
            } else if (field === skilledInfo && Array.isArray(value)) {
              filteredEntry[field] = value.join(", ");
            } else if (Array.isArray(value)) {
              filteredEntry[field] = value.join(", ");
            } else {
              filteredEntry[field] = !isCellBlank(value) ? value : "-";
            }
          });
          return filteredEntry;
        });

        const wsWsp = XLSX.utils.json_to_sheet(wspExportData);
        XLSX.utils.book_append_sheet(wb, wsWsp, wspSheetTabName.trim() || "WSP");

        const exportPrefix = (fileStructure?.name || "SETA_Plan")
          .replace(/[^a-zA-Z0-9_-]/g, "_")
          .substring(0, 30);
        XLSX.writeFile(wb, `${exportPrefix}_WSP_Submission-${Date.now()}.xlsx`);
        toast.success("Plan exported successfully");
        return;
      }

      // Single-tab default export (FASSET, INSETA, etc.)
      let skilledInfo;
      const outputRequiredFields = setaTemplate
        ?.filter((field) =>
          configData?.training_need_workflow
            ? field?.config?.exportSheet
            : field?.config?.exportSheet && !field?.config?.isDepartmentNameField
        ) ?? [];

      const isCellBlank = (v) =>
        v === undefined ||
        v === null ||
        v === "" ||
        (typeof v === "string" && v.trim() === "");

      const filteredData = rows?.map((record) => {
        const filteredEntry = {};

        outputRequiredFields.forEach((fieldConfig) => {
          const field = fieldConfig?.fieldName;
          if (!field) return;
          if (fieldConfig?.config?.isSkillInfoField) {
            skilledInfo = field;
          }
          const isAdditionalField = fieldConfig?.config?.isAdditionalField;
          const value = isAdditionalField
            ? (record.allocated_additional_field?.[field] ?? record[field])
            : record[field];
          const isTrainingCostField = fieldConfig?.config?.isTrainingCostField;
          const isNumberField = fieldConfig?.type === "number";

          if (isTrainingCostField || isNumberField) {
            const num = Number(value);
            if (!isCellBlank(value) && !Number.isNaN(num)) {
              filteredEntry[field] = num;
            } else {
              filteredEntry[field] = "-";
            }
          } else if (field === skilledInfo && Array.isArray(value)) {
            filteredEntry[field] = value.join(", ");
          } else if (Array.isArray(value)) {
            filteredEntry[field] = value.join(", ");
          } else {
            filteredEntry[field] = value ?? "-";
          }
        });

        return filteredEntry;
      });

      const ws = XLSX.utils.json_to_sheet(filteredData);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      XLSX.writeFile(wb, `Plan-Optimizer-${Date.now()}.xlsx`);
      toast.success("Plan exported successfully");
    } catch (error) {
      console.error("Error exporting plan:", error);
      toast.error("Error exporting plan: " + error.message);
    }
  };

  // Handle generate callback from modal
  const handleGenerateCallback = (data) => {
    if (data?.saved) {
      setLastAutoLoadedPlanId(null);
      dispatch(fetchPlanOptimizerHistory(companyId));
      setIsOptimizerModalOpen(false);
      setRegenerateValues(null);
    } else if (data?.regenerate) {
      // Regenerate was clicked - reopen modal with same values
      setRegenerateValues({
        bbeeType: data.bbeeType,
        trainingCategoryType: data.trainingCategoryType,
        criticalSkillsPriority: data.criticalSkillsPriority,
        bbeeValues: data.bbeeValues,
        trainingCategoryValues: data.trainingCategoryValues,
      });
      setIsOptimizerModalOpen(true);
    } else if (data?.allocation_summary) {
      // Initial generation - values already in modal state
      // No action needed
    }
  };

  // Get budget info from response_summary
  const getBudgetInfo = (plan) => {
    if (!plan?.response_summary?.allocation_summary?.budget) {
      return { total: 0, allocated: 0 };
    }
    return plan.response_summary.allocation_summary.budget;
  };

  // Get complete summary data
  const getSummaryData = (plan) => {
    return plan?.response_summary?.allocation_summary || null;
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return Number(num).toLocaleString('en-ZA');
  };

  // Handle summary icon click
  const handleViewSummary = (item) => {
    const summary = getSummaryData(item);
    const budget = getBudgetInfo(item);
    
    if (summary) {
      // Extract slider values from input_data for proper display
      const inputData = item.input_data || {};
      const optimizerConfig = inputData.optimizer_config || {};
      const demographics = optimizerConfig.demographics || {};
      const trainingCategories = optimizerConfig.training_categories || {};
      const allocationType = optimizerConfig.allocation_type || {};
      
      // Convert demographics to bbeeValues format
      const extractedBbeeValues = {};
      Object.keys(demographics).forEach((key) => {
        if (key !== "disability" && key !== "none" && key !== "unemployed") {
          const demo = demographics[key];
          if (Array.isArray(demo) && demo.length >= 2) {
            // Format: [male, female]
            extractedBbeeValues[key] = {
              male: { c: demo[0] || 0, t: demo[0] || 0 },
              female: { c: demo[1] || 0, t: demo[1] || 0 },
            };
          }
        }
      });
      
      // Convert training_categories to trainingCategoryValues format
      const extractedTrainingCategoryValues = {};
      Object.keys(trainingCategories).forEach((key) => {
        const cat = trainingCategories[key];
        if (cat && typeof cat === 'object' && cat.budget !== undefined) {
          extractedTrainingCategoryValues[key] = {
            c: cat.budget || 0,
            t: cat.budget || 0,
          };
        } else if (Array.isArray(cat) && cat.length > 0) {
          extractedTrainingCategoryValues[key] = {
            c: cat[0] || 0,
            t: cat[0] || 0,
          };
        }
      });
      
      // Extract disability data
      const extractedDisabilityData = {};
      if (demographics.disability) {
        const disability = demographics.disability;
        if (Array.isArray(disability) && disability.length > 0) {
          extractedDisabilityData.total = disability[0];
        } else if (typeof disability === 'object' && disability.total !== undefined) {
          extractedDisabilityData.total = disability.total;
        } else if (typeof disability === 'object' && disability.allocation?.total !== undefined) {
          extractedDisabilityData.total = disability.allocation.total;
        }
      }

      // Extract unemployed / none data
      const extractedUnemployedData = { c: 0, t: 0 };
      const noneDemo = demographics.none || demographics.unemployed;
      const noneCompact = optimizerConfig.demographics_none?.none;
      if (Array.isArray(noneCompact) && noneCompact.length > 0) {
        extractedUnemployedData.c = Number(noneCompact[0]) || 0;
        extractedUnemployedData.t = extractedUnemployedData.c;
      } else if (noneDemo) {
        if (Array.isArray(noneDemo) && noneDemo.length > 0) {
          extractedUnemployedData.c = Number(noneDemo[0]) || 0;
          extractedUnemployedData.t = extractedUnemployedData.c;
        } else if (typeof noneDemo === "object") {
          const v =
            Number(noneDemo.allocation?.total ?? noneDemo.total ?? 0) || 0;
          extractedUnemployedData.c = v;
          extractedUnemployedData.t = v;
        }
      }
      
      // Extract critical skills priority
      const extractedCriticalSkillsPriority = 
        optimizerConfig.priority_weights?.critical_skills !== undefined
          ? optimizerConfig.priority_weights.critical_skills
          : (summary?.critical_skills_priority !== undefined
              ? summary.critical_skills_priority
              : null);
      
      // Use stored summary data with input data for context
      setSelectedSummaryData({
        summary: summary,
        inputData: inputData,
        generatedPlan: item.response_summary?.generated_plan,
        // Extract slider values from input_data structure
        bbeeValues: extractedBbeeValues,
        trainingCategoryValues: extractedTrainingCategoryValues,
        disabilityData: extractedDisabilityData,
        unemployedData: extractedUnemployedData,
        bbeeType: (typeof allocationType === 'object' && allocationType.bbee) 
          ? allocationType.bbee 
          : (Array.isArray(allocationType) ? allocationType[0] : "budget"),
        trainingCategoryType: (typeof allocationType === 'object' && allocationType.training_category)
          ? allocationType.training_category
          : (Array.isArray(allocationType) ? allocationType[1] : "budget"),
        criticalSkillsPriority: extractedCriticalSkillsPriority,
      });
      setSummaryModalOpen(true);
    } else if (budget.total > 0) {
      // Fallback: show basic budget info if no summary available
      const inputData = item.input_data || {};
      setSelectedSummaryData({
        summary: {
          budget: budget,
        },
        inputData: inputData,
        generatedPlan: item.response_summary?.generated_plan,
      });
      setSummaryModalOpen(true);
    } else {
      toast.info("No summary data available for this plan");
    }
  };

  return (
    <div className="flex items-center justify-between flex-wrap h-[calc(100vh-68px)] relative">
      {/* Sidebar */}
      <div
        className={`h-full border-r border-solid border-grey-400 transition-all duration-500 ease-in-out overflow-hidden ${
          isAISidebarOpen
            ? "w-[600px] opacity-100"
            : "w-0 border-none opacity-0"
        }`}
      >
        <div className="grid grid-cols-1 grid-rows-[60px_auto] h-full">
          <div className="flex justify-between items-center py-1 border-b border-solid border-grey-300 px-3">
            <p className="2xl:text-xl 2xl:leading-xl text-base leading-base font-semibold text-blue-500">
              Plan Optimizer History
            </p>

            <CButton onClick={() => setIsAISidebarOpen(false)} className="!p-0">
              <ToggleBarIcon className="w-[24px] h-[24px] !stroke-blue-500" />
            </CButton>
          </div>
          <div className="p-4 flex flex-col h-full overflow-hidden">
            <div className="overflow-y-auto h-full rounded-lg border border-solid border-blue-200">
              <table className="w-full border-collapse custom-table ">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      Date
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      Summary
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 px-2 py-2">
                      <Tooltip
                        title="Data Transformation Progress: Processed Employees/Total Employees"
                        placement="top"
                        arrow
                      >
                        <span className="cursor-help">Status</span>
                      </Tooltip>
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 px-2 py-2">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    <tr>
                      <td colSpan="4" className="p-4 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : history?.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="p-4 text-center text-gray-500">
                        No plans found
                      </td>
                    </tr>
                  ) : (
                    history?.map((item) => {
                      const budget = getBudgetInfo(item);
                      const summary = getSummaryData(item);
                      const staleInfo = staleByPlanId[item.id];
                      const isStale = Boolean(staleInfo?.stale);
                      const staleReasons = formatPlanOptimizerStaleReasons(
                        staleInfo?.reasons,
                      );
                      const isPlanApproved = Boolean(item.approved_at);
                      const isTransformationComplete = isPlanTransformationComplete(item);
                      const canViewDashboard = isTransformationComplete;
                      const canApprove =
                        !isPlanApproved && isTransformationComplete && !isStale;
                      const dashboardTooltip = canViewDashboard
                        ? "View in Dashboard"
                        : "Dashboard preview available when plan rows are saved";
                      const approveTooltip = isPlanApproved
                        ? "Plan already approved"
                        : !isTransformationComplete
                          ? "Approve available when plan rows are saved"
                          : isStale
                            ? `Plan is stale: ${staleReasons || "live data changed — regenerate before approving"}`
                            : "Approve";
                      const planActionBtnClass =
                        "!min-w-0 flex-shrink-0 bg-blue-100 text-blue-600 hover:bg-blue-200 !h-[28px] !w-[28px] !p-0 !rounded-[4px] inline-flex items-center justify-center disabled:!opacity-40 disabled:hover:bg-blue-100";

                      return (
                        <tr key={item.id}>
                          <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-1 w-[170px] text-gray-500">
                            {moment(item.created_at).format("DD-MM-YYYY hh:mm A")}
                          </td>
                          <td 
                            className={`text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-1 text-gray-500 ${
                              summary ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''
                            }`}
                            onClick={() => summary && handleViewSummary(item)}
                            title={summary ? "Click to view complete summary" : ""}
                          >
                            {/* Compact Budget Summary */}
                            <div>
                              <p className="text-xs">
                                <span className="font-medium">Total:</span>{" "}
                                <span className="text-blue-500">{formatZAR(budget.total)}</span>
                              </p>
                              <p className="text-xs">
                                <span className="font-medium">Allocated:</span>{" "}
                                <span className="text-blue-500">{formatZAR(budget.allocated)}</span>
                              </p>
                              {budget.percentage !== undefined && (
                                <p className="text-xs">
                                  <span className="font-medium">Usage:</span>{" "}
                                  <span className="text-blue-500">{budget.percentage.toFixed(2)}%</span>
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-2 text-gray-500">
                            {item.scope_captured_at ? (
                              <Tooltip
                                title="Plan ready"
                                placement="top"
                                arrow
                              >
                                <div className="flex justify-center">
                                  <span className="text-xs font-medium text-blue-500">
                                    100%
                                  </span>
                                </div>
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title="Plan rows not saved"
                                placement="top"
                                arrow
                              >
                                <span className="text-xs text-gray-400">-</span>
                              </Tooltip>
                            )}
                          </td>
                          <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm px-2 py-2 text-gray-500">
                            <div className="flex items-center gap-2 min-w-[136px]">
                              <CButton
                                onClick={() => handleViewPlan(item)}
                                tooltip="View Plan"
                                tooltipPosition="top"
                                className={planActionBtnClass}
                              >
                                <ViewIcon className="w-4 h-4" />
                              </CButton>
                              <CButton
                                onClick={() => handleViewDashboard(item)}
                                disabled={!canViewDashboard}
                                tooltip={dashboardTooltip}
                                tooltipPosition="top"
                                className={planActionBtnClass}
                              >
                                <DashboardIcon className="w-4 h-4 !fill-blue-600" />
                              </CButton>
                              <CButton
                                onClick={() => handleApprovePlan(item)}
                                disabled={!canApprove}
                                tooltip={approveTooltip}
                                tooltipPosition="top"
                                className={planActionBtnClass}
                              >
                                <CheckIcon className="w-4 h-4 !fill-blue-600" />
                              </CButton>
                              <CButton
                                onClick={() => handleDeletePlan(item)}
                                tooltip="Delete Plan"
                                tooltipPosition="top"
                                className="!min-w-0 flex-shrink-0 bg-red-100 text-red-600 hover:bg-red-200 !h-[28px] !w-[28px] !p-0 !rounded-[4px] inline-flex items-center justify-center"
                              >
                                <DeleteIcon />
                              </CButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`h-full transition-[width] duration-500 ease-in-out 2xl:px-4 px-3 ${
          isAISidebarOpen ? "w-[calc(100%-600px)]" : "w-full"
        }`}
      >
        <div className="bg-white border border-solid border-blue-200 border-t-0 border-b-0 overflow-hidden">
          <div className="flex items-center justify-between p-2">
            <div className="flex items-center gap-2">
              {!isAISidebarOpen && (
                <CButton
                  className="!p-0 mr-2"
                  onClick={() => setIsAISidebarOpen(true)}
                  type="button"
                >
                  <ToggleBarIcon className="w-[24px] h-[24px] !stroke-blue-500 rotate-180" />
                </CButton>
              )}
              <CTextField
                name="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startIcon={<SearchIcon className="text-blue-300" />}
                placeholder={"Search"}
                className="!mt-0 w-full 2xl:max-w-[300px] max-w-[200px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <CButton
                className="gradient-btn"
                onClick={() => {
                  setRegenerateValues(null);
                  setIsOptimizerModalOpen(true);
                }}
              >
                Optimize
              </CButton>
              <CButton
                className="text-blue-500 hover:text-white hover:bg-blue-500 border border-solid border-blue-500 rounded-lg p-0 inline-flex items-center gap-1 group disabled:opacity-50"
                onClick={handleExport}
                disabled={!rows?.length}
              >
                <DownloadIcon className="fill-blue-500 group-hover:fill-white" />
                WSP File
              </CButton>
            </div>
          </div>

          <div className="h-[calc(100vh-134px)] w-full relative gen-training-table">
            <CDataGrid
              searchQuery={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              rows={displayRows}
              columns={columns || []}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              sortingMode="server"
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(event) => {
                setPageSize(parseInt(event.target.value, 10));
                setPage(0);
              }}
              idKey="id"
              loading={isLoading}
              className="bg-white shadow-md rounded-lg"
            />

            {/* Bottom section matching Skills Planner UI */}
            <div className="absolute bottom-[6px] left-[12px] h-[70px] w-[calc(100%-384px)]">
              <div className="flex gap-x-2 items-center flex-wrap">
                {(selectedPlan || latestPlanUpdate?.plan) && (
                  <div className="flex flex-col gap-1">
                    {(() => {
                      const planToDisplay = selectedPlan || latestPlanUpdate.plan;
                      const budgetInfo = getBudgetInfo(planToDisplay);
                      return (
                        <p className="text-gray-700 font-semibold text-sm leading-sm w-full">
                          Total Allocated Budget:
                          <span className="text-blue-500 font-semibold mx-1">
                            {formatZAR(budgetInfo.allocated || 0)}
                          </span>
                        </p>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Optimizer Modal */}
      <PlanOptimizerModal
        open={isOptimizerModalOpen}
        onClose={() => {
          setIsOptimizerModalOpen(false);
          setRegenerateValues(null);
        }}
        onGenerate={handleGenerateCallback}
        initialValues={regenerateValues}
      />

      {/* Summary View Modal */}
      {selectedSummaryData && (
        <AllocationSummaryModal
          open={summaryModalOpen}
          onClose={() => {
            setSummaryModalOpen(false);
            setSelectedSummaryData(null);
          }}
          readOnly={true}
          storedSummary={selectedSummaryData.summary}
          inputData={selectedSummaryData.inputData}
          generatedPlan={selectedSummaryData.generatedPlan}
          totalBudget={selectedSummaryData.summary?.budget?.total || 0}
          bbeeValues={selectedSummaryData.bbeeValues || {}}
          trainingCategoryValues={selectedSummaryData.trainingCategoryValues || {}}
          disabilityData={selectedSummaryData.disabilityData || {}}
          unemployedData={selectedSummaryData.unemployedData || {}}
          bbeeType={selectedSummaryData.bbeeType}
          trainingCategoryType={selectedSummaryData.trainingCategoryType}
          criticalSkillsPriority={selectedSummaryData.criticalSkillsPriority}
        />
      )}

      <CommentDialog
        open={approveDialogState.show}
        onClose={() =>
          setApproveDialogState({
            plan: null,
            show: false,
            comment: "",
            submitting: false,
          })
        }
        onSubmit={handleConfirmApprovePlan}
        description="A comment is required for audit history."
        comment={approveDialogState.comment}
        onCommentChange={(e) =>
          setApproveDialogState((s) => ({
            ...s,
            comment: e.target.value,
          }))
        }
        loading={approveDialogState.submitting}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogState.show}
        onClose={() =>
          !deleteDialogState.submitting && resetDeleteDialogState()
        }
        onConfirm={handleConfirmDeletePlan}
        title="Delete Plan"
        description="Are you sure you want to delete this plan? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleteDialogState.submitting}
        confirmClass="bg-red-500 text-white hover:bg-red-700 border border-red-500 border-red-700 px-4 py-2 rounded-lg"
        cancelClass="bg-white text-gray-700 hover:text-white hover:bg-gray-500 border border-gray-500 px-4 py-2 rounded-lg"
      />
    </div>
  );
};

export default PlanOptimizer;
